import { Form as FormikForm, Formik } from 'formik';
import Quill from 'quill';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Avatar } from '@hivebrite/design-system/Avatar';
import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';

import mentionsOptions from 'hb-react/shared/components/DesignSystem/QuillEditor/utils/mentionsOptions';
import { shouldConsiderFormValid } from 'hb-react/shared/components/formik/utils/shouldDisplayError';
import QuillEditorWrappedV3 from 'hb-react/shared/components/QuillEditorWrappedV3';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { isAnEmptyHtmlContent } from 'helpers/isAnEmptyHtmlContent';

import { createComment, updateComment } from '../utils/actions';

const Delta = Quill.import('delta');

export const CommentForm = forwardRef(
  (
    {
      autoFocus,
      canDisplayEmojiPicker,
      commentableId = -1,
      commentableType = '',
      commentId = -1,
      externalMention,
      handleOnFocus,
      initialComment,
      isReply = false,
      onBlur,
      onCloseEdition,
      onComment,
      user,
    },
    quillRef,
  ) => {
    const boxRef = useRef();
    const formikRef = useRef();
    const quillEditorRefHelper = useRef();
    const { flagEvUserPicturesToGlobalStorage } = useFeatureFlag();

    const quillEditorRef = quillRef || quillEditorRefHelper;

    const [currentComment, setCurrentComment] = useState(initialComment);

    const initialValues = useMemo(
      () => ({
        comment: {
          content_html: currentComment?.content_html || '',
          storage_uploads: currentComment?.uploads || [],
          storage_attachments: currentComment?.attachments || [],
          embed: currentComment?.embed,
        },
      }),
      [
        currentComment?.attachments,
        currentComment?.content_html,
        currentComment?.uploads,
        currentComment?.embed,
      ],
    );

    const [isOnFocus, setIsOnFocus] = useState(autoFocus);
    const quillEditorInputName = 'comment[content_html]';
    const quillEditorUploadsName = 'comment[storage_uploads]';
    const quillEditorAttachmentsName = 'comment[storage_attachments]';
    const embedInputName = 'comment[embed]';
    const requiredErrorMessage = __(
      "Text can't be empty. Add a message to publish your comment",
    );

    const defaultMinHeightQuillEditor = 100;
    const minHeightQuillEditor = !isAnEmptyHtmlContent(
      currentComment?.content_html ?? '',
    )
      ? defaultMinHeightQuillEditor
      : undefined;

    const cleanForm = useCallback(() => {
      const { resetForm } = formikRef.current;

      resetForm({ values: initialValues });

      quillEditorRef?.current?.clearAllContents();
    }, [quillEditorRef, formikRef, initialValues]);

    const onResponse = useCallback(
      ({ comment }) => {
        setCurrentComment(null);
        cleanForm();
        handleOnFocus();
        onComment({ comment, newComment: comment, commentId });
      },
      [onComment, cleanForm, commentId, handleOnFocus],
    );

    const onEditSuccess = useCallback(
      ({ comment }) => {
        setCurrentComment(null);
        cleanForm();
        handleOnFocus?.();
        onComment({ comment, newComment: comment, commentId });
        onCloseEdition?.();
      },
      [onComment, onCloseEdition, cleanForm, commentId, handleOnFocus],
    );

    const handleSubmit = useCallback(
      async (values, formik) => {
        try {
          if (currentComment) {
            const response = await updateComment({
              comment: {
                commentable_id: currentComment.commentable_id,
                commentable_type: currentComment.commentable_type,
                content: values.comment.content_html,
                embed: values.comment.embed,
                id: currentComment.id,
                storage_uploads: values.comment.storage_uploads,
                storage_uploads_attachments_attributes:
                  values.comment.storage_uploads_attachments_attributes,
                storage_attachments: values.comment.storage_attachments,
                storage_attachments_attachments_attributes:
                  values.comment.storage_attachments_attachments_attributes,
              },
              currentUser: user,
            });

            onEditSuccess(response);
          } else {
            const response = await createComment({
              comment: {
                commentable_id: commentableId,
                commentable_type: commentableType,
                content: values.comment?.content_html,
                embed: values.comment?.embed,
                storage_uploads: values.comment.storage_uploads,
                storage_uploads_attachments_attributes:
                  values.comment.storage_uploads_attachments_attributes,
                storage_attachments: values.comment.storage_attachments,
                storage_attachments_attachments_attributes:
                  values.comment.storage_attachments_attachments_attributes,
              },
              currentUser: user,
            });

            onResponse(response);
          }
        } catch (_) {
          formik.setStatus(__('Sorry, an error occurred'));
        }
      },
      [
        currentComment,
        user,
        onEditSuccess,
        commentableId,
        commentableType,
        onResponse,
      ],
    );

    const handleRemoveEmbed = useCallback(() => {
      formikRef.current?.setFieldValue(embedInputName, {});
    }, [formikRef]);

    // Set Embed inside Quill after a user add a link
    const handleAddEmbedWithLink = useCallback(
      (url) => {
        quillEditorRef.current && quillEditorRef.current.addEmbed(url);
      },
      [quillEditorRef],
    );

    const handleExternalMention = useCallback(() => {
      const { mention, char } = externalMention;

      const contentEditableRef = quillEditorRef.current.getContentEditableRef();

      contentEditableRef.focus();

      const index = 0;
      const { id, display } = mentionsOptions.linkRenderer(mention, char);

      contentEditableRef.insertText(index, display, 'mention', id);

      // We add an empty space after the mention
      const delta = new Delta().retain(index + display.length).insert(' ');

      contentEditableRef.updateContents(delta);
      contentEditableRef.setSelection(index + display.length + 1, 0);
    }, [externalMention, quillEditorRef]);

    const handleFocus = useCallback(() => {
      setIsOnFocus(true);
    }, []);

    const handleBlur = useCallback(() => {
      if (
        quillEditorRef.current &&
        !quillEditorRef.current.getForceEditorFocusRef()
      ) {
        setTimeout(() => {
          setIsOnFocus(false);

          if (onBlur) {
            onBlur();
          }
        }, 250);
      }
    }, [quillEditorRef, onBlur]);

    useEffect(() => {
      if (externalMention) {
        cleanForm();
        handleExternalMention();
      }
    }, [externalMention, handleExternalMention, cleanForm]);

    return (
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {(formik) => {
          formikRef.current = formik;

          const hasContent = !isAnEmptyHtmlContent(
            formik.values.comment?.content_html ?? '',
          );

          return (
            <FormikForm style={{ width: '100%' }}>
              <Box ref={boxRef} width="100%" pb={6}>
                <Avatar
                  alt=""
                  src={
                    flagEvUserPicturesToGlobalStorage
                      ? user?.new_photo?.thumbnail_url
                      : user?.photo_thumb_url
                  }
                  size={32}
                  minWidth={32}
                />

                <Box flexDirection="column" pl={2} width="100%" minWidth={0}>
                  <QuillEditorWrappedV3
                    autoFocus={isOnFocus}
                    isRequired
                    requiredErrorMessage={requiredErrorMessage}
                    ariaLabel={quillEditorInputName}
                    ariaLabelledBy={quillEditorInputName}
                    compact
                    containerElement={boxRef?.current}
                    id={quillEditorInputName}
                    name={quillEditorInputName}
                    uploadsFieldName={quillEditorUploadsName}
                    attachmentsFieldName={quillEditorAttachmentsName}
                    isDisabled={formik.isSubmitting}
                    initialEmbed={initialValues.comment.embed}
                    minHeight={minHeightQuillEditor}
                    onAddLink={handleAddEmbedWithLink}
                    onAddEmbed={(embed) =>
                      formik.setFieldValue(embedInputName, embed)
                    }
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    onRemoveEmbed={handleRemoveEmbed}
                    options={{
                      embedLy: true,
                      toolbar:
                        hasContent || isOnFocus
                          ? {
                              images: { multiple: false },
                              attachments: true,
                              links: true,
                              emojiPicker: canDisplayEmojiPicker,
                              mentions: mentionsOptions,
                            }
                          : undefined,
                      toolbarFloats: [
                        {
                          key: 'link',
                          items: [
                            {
                              type: 'link',
                            },
                          ],
                        },
                      ],
                    }}
                    placeholder={
                      isReply
                        ? I18n.t('web.comments.write_a_reply')
                        : I18n.t('web.comments.add')
                    }
                    ref={quillEditorRef}
                    singleImageUpload
                    disableValidationOnBlur
                  />

                  {currentComment && onCloseEdition ? (
                    <Box>
                      <Box pt={4}>
                        <Button variant="secondary" onClick={onCloseEdition}>
                          {__('Cancel')}
                        </Button>
                        <Button
                          type="submit"
                          isLoading={formik.isSubmitting}
                          isDisabled={
                            !hasContent ||
                            formik.isSubmitting ||
                            !shouldConsiderFormValid({
                              isValid: formik.isValid,
                              submitCount: formik.submitCount,
                              touched: formik.touched,
                              errors: formik.errors,
                            })
                          }
                          ml={3}
                        >
                          {__('Update')}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    hasContent && (
                      <Box pt={4}>
                        <Button
                          type="submit"
                          isLoading={formik.isSubmitting}
                          isDisabled={
                            formik.isSubmitting ||
                            !shouldConsiderFormValid({
                              isValid: formik.isValid,
                              submitCount: formik.submitCount,
                              touched: formik.touched,
                              errors: formik.errors,
                            })
                          }
                          data-testid="button-submit-comment"
                        >
                          {isReply ? __('Reply') : __('Comment')}
                        </Button>
                      </Box>
                    )
                  )}
                </Box>
              </Box>
            </FormikForm>
          );
        }}
      </Formik>
    );
  },
);
